import React, { useEffect, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { entries } from '../../../helpers/common';
import { readableNumber, validateAddOn } from '../../../helpers/business';
import { getPmImgSrc } from '../../../helpers/media';
import {
  usePromiseWrapper,
  useCustomTranslation,
  useStoreValidator,
  useStoreSelector,
  useActions,
} from '../../../helpers/hooks';
import { createInvoice } from '../../../actions/invoice';
import arrowDownIcon from '../../../imgs/arrow-down-icon.svg';
import selectedArrowIcon from '../../../imgs/selected-arrow-icon.svg';

import ErrorWrapper from '../../ErrorWrapper';
import Loader from '../../Loader';
import DelayedRedirect from '../../DelayedRedirect';
import Tooltip from '../../Tooltip';

const ErrCode = {
  PAYER_AMOUNT_IS_TOO_SMALL: 102,
  PAYER_AMOUNT_IS_TOO_LARGE: 103,

  TEST: 404,
};

function addOnsReducer(state, action) {
  switch (action.type) {
    case 'updateField': {
      const { id, name, value } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          [name]: {
            ...(state[id] || {})[name],
            value,
          },
        },
      };
    }
    case 'validateField': {
      const { id, name, valid } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          [name]: {
            ...(state[id] || {})[name],
            valid,
          },
        },
      };
    }
    default:
      return state;
  }
}

function updateAddOnFor(id) {
  return function updateAddOn(name, value) {
    return {
      type: 'updateField',
      payload: {
        id,
        name,
        value,
      },
    };
  };
}

function validateAddOnFor(id) {
  return function validate(name, template, value) {
    const valid = validateAddOn(template, value);
    return {
      type: 'validateField',
      payload: {
        id,
        name,
        valid,
      },
    };
  };
}

function AddOn({ addOn }) {
  const {
    name,
    comment,
    example,
    label,
    prefix,
    value,
    valid,
    update,
    validate,
    options,
  } = addOn;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const {
    t,
    i18n: { language },
  } = useCustomTranslation();

  useEffect(() => {
    if (options && options.length > 0) {
      update(options[0].value);
    }
  }, []);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  return (
    <div className="block__inline">
      <div>{label}</div>
      <div>
        {options && options.length > 0 ? (
          <div className="select">
            <div className="select--list--header" onClick={toggleDropdown}>
              <span>
                {
                  options.find((option) => option.value === value)?.label[
                    language
                  ]
                }
              </span>
              <img
                src={arrowDownIcon}
                alt=""
                className={classnames('select--dropdown--arrow', {
                  'arrow-reversed': isDropdownOpen,
                })}
              />
            </div>
            {isDropdownOpen && (
              <div className="select--dropdown">
                <ul className="select--list">
                  {options.map((option) => {
                    const onClick = () => {
                      update(option.value);
                      toggleDropdown();
                    };
                    const isSelected = value === option.value;
                    return (
                      <li
                        key={option.value}
                        className="select--list--item"
                        onClick={onClick}
                      >
                        <span>{option.label[language]}</span>
                        {isSelected && (
                          <img src={selectedArrowIcon} alt="selected" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <>
            <span
              className={classnames('input__prefix input--styled text--db', {
                'input--error': !valid,
              })}
            >
              {prefix}
            </span>
            <input
              type="text"
              name={name}
              className={classnames('input--styled ', {
                'input--error': !valid,
              })}
              placeholder={` ${example}`}
              value={value}
              onChange={(e) => {
                if (!valid) {
                  validate(e.target.value);
                }
                update(e.target.value);
              }}
              onBlur={(e) => {
                validate(e.target.value);
              }}
            />
          </>
        )}

        {comment ? <p className="tip">{comment}</p> : null}

        <p className={classnames('tip text--error', { 'hide--i': valid })}>
          {t('PayWays.InvalidAddOn')}
        </p>
      </div>
    </div>
  );
}

export default function CreationCard({
  detailError,
  payWay,
  siblingPayWays,
  payMethod,
  selectPayWay,
  handlePayWayChange,

  onRedirect,
}) {
  const {
    urlFor,
    t,
    i18n: { language },
  } = useCustomTranslation();

  // CheckBox
  const [agreementChecked, setAgreementChecked] = useState(true);

  // function toggleAgreement() {
  //   setAgreementChecked(!agreementChecked);
  // }

  // Modal
  // const [modalOpen, setModalOpen] = useState(false);

  // function openModal() {
  //   setModalOpen(true);
  // }

  // function closeModal() {
  //   setModalOpen(false);
  // }

  // AddOns handling
  const [state, dispatch] = useReducer(addOnsReducer, {});

  const { addOns: addOnTemplates } = payWay.details || {};

  const updateAddOn = updateAddOnFor(payWay.id);
  const validateAddOn = validateAddOnFor(payWay.id);
  const addOnsState = state[payWay.id] || {};

  let addOns;
  if (!detailError) {
    addOns = entries(addOnTemplates || {}).map(([name, template]) => {
      const { value = '', valid = true } = addOnsState[name] || {};

      return {
        name,
        comment: template.comment ? template.comment[language] : null,
        example: template.example,
        label: template.label ? template.label[language] : null,
        prefix: template.prefix,
        options: template.options,
        value,
        valid,
        update: (value) => dispatch(updateAddOn(name, value)),
        validate: (value) => {
          const action = validateAddOn(name, template, value);

          dispatch(action);

          return action.payload.valid;
        },
      };
    });
  }

  function addOnsValid() {
    return !addOns.find(({ valid }) => !valid);
  }

  // Invoice Creation
  const [performRedirect, setPerformRedirect] = useState(false);

  const { orderUnique } = useStoreSelector(
    (selector) => selector.getShop(),
    []
  );

  const invoiceCreated = useStoreValidator((validator) =>
    validator.invoiceCreated()
  );
  const createInvoiceAction = useActions(createInvoice, []);

  const [wrappedCreateInvoice, invoiceLoading, invoiceError] =
    usePromiseWrapper({
      initPromise() {
        return createInvoiceAction(
          payWay.id,
          addOns.reduce(
            (previous, { name, value }) => {
              if (value != null && value !== '') {
                previous[name] = value;
              }

              return previous;
            },
            { lang: language }
          )
        );
      },
      initialState: {
        loading: false,
      },
    });

  async function onButtonClick() {
    if (invoiceLoading || buttonDisabled) return;

    let performCreate = true;
    addOns.forEach(({ value, validate }) => {
      if (!validate(value)) {
        performCreate = false;
      }
    });

    if (performCreate) {
      await wrappedCreateInvoice();

      setPerformRedirect(true);
      if (typeof onRedirect === 'function') onRedirect();
    }
  }

  const buttonDisabled = !agreementChecked || !!detailError || !addOnsValid();

  function withCurrency(value) {
    if (value == null || isNaN(value)) return null;

    return `${readableNumber(value, 2)} ${payWay.currency}`;
  }

  const payment = useStoreSelector((selector) => selector.getPayment());

  const filteredSiblingsPayWay = siblingPayWays.filter(
    ({ id }) => payment.initialPayWay == null || id === payment.initialPayWay
  );

  const invoiceErrorCode = useMemo(() => {
    if (!invoiceError) {
      return;
    }
    if (invoiceError.data.errCode === 404) {
      return `ErrorCodes.${invoiceError.data.errCode}`;
    }
  }, [invoiceError]);

  const detailErrorWithParams = useMemo(() => {
    if (detailError == null) return;

    if (
      detailError.data &&
      detailError.data.errCode &&
      detailError.data.message
    ) {
      switch (detailError.data.errCode) {
        case ErrCode.PAYER_AMOUNT_IS_TOO_SMALL:
          return {
            ...detailError,
            data: {
              ...detailError.data,
              params: {
                amount: detailError.data.message.match(/min: (.*)$/)[1],
              },
            },
          };
        case ErrCode.PAYER_AMOUNT_IS_TOO_LARGE:
          return {
            ...detailError,
            data: {
              ...detailError.data,
              params: {
                amount: detailError.data.message.match(/max: (.*)$/)[1],
              },
            },
          };
        default:
          return;
      }
    }
  }, [detailError]);

  return (
    <>
      {invoiceCreated && (performRedirect || orderUnique) ? (
        <DelayedRedirect delay={450} push to={urlFor('payment')} />
      ) : null}
      {/*{modalOpen ? <Modal close={closeModal} /> : null}*/}

      {invoiceLoading ? <Loader blur="true" /> : null}

      <div className="card-info block">
        <h3>{t('PayWays.Requisites')}</h3>

        <div className="block__text">
          {payMethod.name ? (
            <div className="block__inline">
              <div>{t('PayWays.Method')}:</div>

              <div className="card-info__method">
                <img src={getPmImgSrc(payMethod.id)} alt="" /> {payMethod.name}
              </div>
            </div>
          ) : null}

          {detailError ? null : addOns.map((addOn) => <AddOn addOn={addOn} />)}

          <div className="block__inline">
            <div>{t('PayWays.Currency')}:</div>
            <div>
              <ul className="currancy">
                {filteredSiblingsPayWay.map(({ id, currency }) => (
                  <li
                    key={id}
                    onClick={() => {
                      if (id === payWay.id) return;

                      handlePayWayChange(id);
                    }}
                    className={classnames({
                      'currancy--selected': id === payWay.id,
                    })}
                  >
                    {currency}
                  </li>
                ))}
              </ul>

              {detailErrorWithParams ? (
                <ErrorWrapper error={detailErrorWithParams} type="PayWays">
                  {(text) => <p className="text--error tip">{text}</p>}
                </ErrorWrapper>
              ) : invoiceErrorCode ? (
                <p className="text--error tip">{t(invoiceErrorCode)}</p>
              ) : invoiceError ? (
                <p className="text--error tip">
                  {(invoiceError.data && invoiceError.data.message) ||
                    t('Invoice.Error')}
                </p>
              ) : null}

              <p className="tip">{t('PayWays.CurrencyComment')}</p>
            </div>
          </div>

          <div className="block__inline">
            <div>{t('PayWays.AmountInCur')}:</div>
            <div className="text--strong">
              {detailError
                ? '-'
                : withCurrency(
                    readableNumber(payWay.details.payerPrice, 2) -
                      (readableNumber(payWay.details.payerFee, 2) -
                        readableNumber(payWay.details.exchFee, 2))
                  )}
            </div>
          </div>

          <div className="block__inline">
            <div>
              {t('PayWays.Fee')}:
              <Tooltip text={t('PayWays.CommissionTip')} />
            </div>
            <div className="text--strong">
              {detailError
                ? '-'
                : withCurrency(
                    readableNumber(payWay.details.payerFee, 2) -
                      readableNumber(payWay.details.exchFee, 2)
                  )}
            </div>
          </div>
        </div>

        <hr />

        <div className="block__inline">
          <div className="card-info__total-text">{t('PayWays.Amount')}:</div>
          <div className="card-info__total">
            {detailError ? '-' : withCurrency(payWay.details.payerPrice)}
          </div>
        </div>

        <div className="card-info__paybtn">
          <button
            className="btn"
            disabled={buttonDisabled}
            onClick={onButtonClick}
          >
            {t('PayWays.Pay')}
          </button>

          <p className="tip checkbox-terms text--center">
            {/*<img*/}
            {/*  src={agreementChecked ? checkboxTrue : checkboxFalse}*/}
            {/*  alt=""*/}
            {/*  onClick={toggleAgreement}*/}
            {/*/>*/}
            {/*<span>{t('PayWays.Agree')}</span>*/}

            {/*<a*/}
            {/*  className="link user-agreement"*/}
            {/*  href="#user-agreement"*/}
            {/*  onClick={openModal}*/}
            {/*>*/}
            {/*  {t('PayWays.WithTerms')} T&C*/}
            {/*</a>*/}
          </p>
        </div>
      </div>
    </>
  );
}

CreationCard.propTypes = {
  payWay: PropTypes.object.isRequired,
  siblingPayWays: PropTypes.array.isRequired,
  payMethod: PropTypes.object.isRequired,
  selectPayWay: PropTypes.func.isRequired,
};
